import { inject, Injectable, makeStateKey, StateKey } from '@angular/core';

import { catchError, EMPTY, filter, map, mergeMap, Observable, of, switchMap, take } from 'rxjs';

import { GlobalBookingTravelClass, MonitoringLogger, StateTransferService } from '@fcom/core';
import { ConfigService } from '@fcom/core/services/config/config.service';
import {
  DestinationSearchItem,
  DestinationSearchItemType,
} from '@fcom/destination-search/interfaces/destination-search.interface';
import { DestinationSearchService } from '@fcom/destination-search/services/destination-search.service';
import { flattenDestinations } from '@fcom/destination-search/utils/flatten';
import { isNotEmpty } from '@fcom/core/utils/utils';
import { PageMetaService } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { DapiService } from '@fcom/dapi/api/dapi.service';

import { getPricesForFlights } from '../../../dapi/api/fn/instantsearch/get-prices-for-flights';
import { MarketingOffer } from '../../interfaces/marketing-offer.interface';
import { mapToMarketingOffers } from '../../utils/marketing-offer.utils';

@Injectable()
export class CheapestPriceForAllDestinationsService {
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);
  private stateTransferService = inject(StateTransferService);
  private destinationSearchService = inject(DestinationSearchService);
  private dapiService = inject(DapiService);
  private pageMetaService = inject(PageMetaService);
  private languageService = inject(LanguageService);

  private marketingOffers$: Observable<MarketingOffer[]>;
  private marketingOffersOneWay$: Observable<MarketingOffer[]>;
  private marketingOffersBothWay$: Observable<MarketingOffer[]>;
  private marketingOffersByOriginOneWay: Record<string, Observable<MarketingOffer[]>> = {};
  private marketingOffersByOriginBothWay: Record<string, Observable<MarketingOffer[]>> = {};

  static createStateKey = (originLocationCode: string, tripTypes: TripType[]): StateKey<MarketingOffer[]> =>
    makeStateKey<MarketingOffer[]>(`mos-${originLocationCode}${tripTypes.toString()}`);

  constructor() {
    const configService = this.configService;

    if (!configService.cfg.showMarketingOffers) {
      this.marketingOffers$ = EMPTY;
      this.marketingOffersOneWay$ = EMPTY;
      this.marketingOffersBothWay$ = EMPTY;
      return;
    }
    this.marketingOffers$ = this.pageMetaService.defaultOriginLocationCode$.pipe(
      switchMap((origin) => this.getMarketingOffers(origin, [TripType.RETURN])),
      catchError((e: unknown) => this.handleError(e, [TripType.RETURN])),
      finShare()
    );

    this.marketingOffersOneWay$ = this.pageMetaService.defaultOriginLocationCode$.pipe(
      switchMap((origin) => this.getMarketingOffers(origin, [TripType.ONEWAY])),
      catchError((e: unknown) => this.handleError(e, [TripType.ONEWAY])),
      finShare()
    );

    this.marketingOffersBothWay$ = this.pageMetaService.defaultOriginLocationCode$.pipe(
      switchMap((origin) => this.getMarketingOffers(origin, [TripType.ONEWAY, TripType.RETURN])),
      catchError((e: unknown) => this.handleError(e, [TripType.ONEWAY, TripType.RETURN])),
      finShare()
    );
  }

  offerForSingleTrip(destination: string, oneWay = false, origin?: string): Observable<MarketingOffer> {
    const offers$ = origin
      ? this.getOffers(oneWay, origin)
      : oneWay
        ? this.marketingOffersOneWay$
        : this.marketingOffers$;
    return offers$.pipe(
      map((marketingOffers: MarketingOffer[]) =>
        marketingOffers.filter(
          (marketingOffer) => marketingOffer.destination === destination && marketingOffer.origin !== destination
        )
      ),
      filter(isNotEmpty),
      map((data: MarketingOffer[]): MarketingOffer => ({
        ...data[0],
        prices: data[0].prices.filter((price) =>
          oneWay ? price.price.tripType === TripType.ONEWAY : price.price.tripType === TripType.RETURN
        ),
      }))
    );
  }

  offerFor(destination: string, tripType: TripType): Observable<MarketingOffer> {
    return this.marketingOffersBothWay$.pipe(
      map((marketingOffers: MarketingOffer[]) =>
        marketingOffers.filter((marketingOffer) => marketingOffer.destination === destination)
      ),
      filter(isNotEmpty),
      map((data: MarketingOffer[]): MarketingOffer => {
        const targetPrice = data[0].prices.filter((offer) => offer.price.tripType === tripType);
        return { ...data[0], prices: targetPrice };
      })
    );
  }

  offers(): Observable<MarketingOffer[]> {
    return this.marketingOffers$;
  }

  oneWayOffers(selectedOriginLocationCode?: string): Observable<MarketingOffer[]> {
    return this.getOffers(true, selectedOriginLocationCode);
  }

  bothWayOffers(selectedOriginLocationCode?: string): Observable<MarketingOffer[]> {
    return this.getOffers(false, selectedOriginLocationCode);
  }

  private getOffers(isOneWay: boolean, originLocationCode?: string): Observable<MarketingOffer[]> {
    if (originLocationCode) {
      const tripTypes = isOneWay ? [TripType.ONEWAY] : [TripType.ONEWAY, TripType.RETURN];
      const offers = isOneWay ? this.marketingOffersByOriginOneWay : this.marketingOffersByOriginBothWay;

      if (!offers[originLocationCode]) {
        offers[originLocationCode] = this.getMarketingOffers(originLocationCode, tripTypes).pipe(
          catchError((e: unknown) => this.handleError(e, tripTypes)),
          finShare()
        );
      }

      return offers[originLocationCode];
    }

    return isOneWay ? this.marketingOffersOneWay$ : this.marketingOffersBothWay$;
  }

  private getMarketingOffers(originLocationCode: string, tripTypes: TripType[]): Observable<MarketingOffer[]> {
    return this.stateTransferService.wrapToStateCache(
      CheapestPriceForAllDestinationsService.createStateKey(originLocationCode, tripTypes),
      () => {
        return this.destinationSearchService.destinationSearch().pipe(
          take(1),
          map((destinationItems: DestinationSearchItem[]) => {
            return flattenDestinations(destinationItems)
              .filter((item) => item.type === DestinationSearchItemType.DESTINATION && item.locationCode)
              .map((item) => item.locationCode);
          }),
          mergeMap((destinationLocationCodes: string[]) =>
            this.dapiService
              .invoke$(getPricesForFlights, this.configService.cfg.instantSearchUrl, {
                departureLocationCodes: [originLocationCode],
                destinationLocationCodes: destinationLocationCodes.sort(),
                travelClasses: [
                  GlobalBookingTravelClass.ECONOMY.toLowerCase(),
                  GlobalBookingTravelClass.ECOPREMIUM.toLowerCase(),
                  GlobalBookingTravelClass.BUSINESS.toLowerCase(),
                ],
                tripTypes,
                locale: this.languageService.langValue,
              })
              .pipe(catchError(() => of({ prices: {} })))
          ),
          map(mapToMarketingOffers)
        );
      }
    );
  }

  private handleError(error: unknown, tripTypes: string[]): Observable<[]> {
    this.monitoringLogger.error(`Error getting destination ${tripTypes.toString()} offer data`, { error });
    return of([]);
  }
}
